import React, { useEffect } from 'react';
import Popup from 'reactjs-popup';
import { toast } from 'react-toastify';
import { Card, Container, Row, Col } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle, faX } from '@fortawesome/free-solid-svg-icons';
import QuoteCostBreakdown from './QuoteCostBreakdown';
import Button from '../Parts/Button';
import Money from '../Parts/Money';
import { useQuoteById } from '../Store/entities/quotes/hooks';
import apiCalls from '../PowerShadesAPIFunctions';
import { successResp } from '../powershadesApiTypes';
import { UsePortalDispatch } from '../Store';
import { RefreshQuotePricing } from '../Store/entities/quotes';

type ApprovalType = 'sales' | 'service';

type ApprovalModalProps = {
	isOpen: boolean;
	onClose: () => void;
	quoteId: number;
	onApprove?: () => void;
	type: ApprovalType;
};

const ApprovalModal = ({ isOpen, onClose, quoteId, onApprove, type }: ApprovalModalProps) => {
	const [quoteCostBreakdownOpen, setQuoteCostBreakdownOpen] = React.useState(false);
	const [approving, setApproving] = React.useState(false);
	const [rejecting, setRejecting] = React.useState(false);

	const quote = useQuoteById(quoteId);

	const onCloseHandler = () => {
		if (!quoteCostBreakdownOpen) {
			onClose();
		}
	};

	const { cost_snapshot: costSnapshot } = useQuoteById(quoteId);

	const [invoiceTotal, setInvoiceTotal] = React.useState<null | number>(null);

	useEffect(() => {
		apiCalls.getQuoteInvoiceSnapshot(quoteId).then((resp) => {
			setInvoiceTotal(resp.data?.invoice?.total !== undefined ? Number(resp.data.invoice.total) : null);
		});
	}, [quoteId]);

	const isInvoiceLow = (invoiceTotal ?? 0) < (costSnapshot ?? 0);
	const isDistLow = (quote?.dist_sale_price_snapshot ?? 0) < (costSnapshot ?? 0);
	const isDealerLow = (quote?.dealer_sale_price_snapshot ?? 0) < (costSnapshot ?? 0);

	const successOrErrorCssClassInvoice = isInvoiceLow  ? 'text-danger' : 'text-primary';
	const successOrErrorCssClassDist = isDistLow ? 'text-danger' : 'text-primary';
	const successOrErrorCssClassDealer = isDealerLow ? 'text-danger' : 'text-primary';

	const hasCouponDiscount = (quote?.coupon_snapshot ?? 0) > 0;
	const hasDiscountReason = quote?.discount_reason && quote.discount_reason.trim().length > 0;
	const showCouponInfo = type === 'service';

	const isSales = type === 'sales';
	const modalTitle = type === 'sales' ? 'Sales Approval' : 'Service Approval';

	const dispatch = UsePortalDispatch();

	useEffect(() => {
			if( isOpen) {
				dispatch(RefreshQuotePricing({quoteId}));
			}
		}, [isOpen]);

	const handleApproveOrder = async () => {
		setApproving(true);
		try {
			let response: null | { data: successResp; error?: any; status: number; } = null;
			if(isSales)
			{
				response = await apiCalls.salesApproveOrder(quoteId);
			} else {
				response = await apiCalls.approveQuoteReview(quoteId);
			}
			if (response.status === 200) {
				toast.success("Order approved successfully!");
				onApprove?.();
				onClose();
				window.location.reload();
			} else {
				toast.error("Error approving order. Please Contact Engineering.");
			}
		} catch (error) {
			toast.error("Error approving order. Please Contact Engineering.");
		} finally {
			setApproving(false);
		}
	};

	const handleRejectOrder = async () => {
		setRejecting(true);
		try {
			let response: null | { data: successResp; error?: any; status: number; } = null;

			if (isSales) {
				response = await apiCalls.salesRejectOrder(quoteId);
			} else {
				response = await apiCalls.rejectQuoteReview(quoteId);
			}

			if (response.status === 200) {
				toast.success("Order rejected successfully!");
				onApprove?.();
				onClose();
				window.location.reload();
			} else {
				toast.error("Error rejecting order. Please Contact Engineering.");
			}
		} catch (error) {
			toast.error("Error rejecting order. Please Contact Engineering.");
		} finally {
			setRejecting(false);
		}
	};

	return (
		<Popup
			closeOnDocumentClick={false}
			closeOnEscape={false}
			className="mobile-modal"
			open={isOpen}
			modal
			onClose={onCloseHandler}
		>
			{quoteCostBreakdownOpen ? (
				<QuoteCostBreakdown
					quoteId={quoteId}
					onClose={() => {
						setQuoteCostBreakdownOpen(false);
					}}
				/>
			) : (
				<Container fluid className="p-0">
					<Card className="border-0 shadow-sm">
						<Card.Header className="d-flex align-items-center">
						<h4 style={{ flex: 1, textAlign: 'center', margin: 0 }}>{modalTitle}</h4>
							<FontAwesomeIcon
								icon={faX}
								className="close-icon link-secondary ms-3"
								onClick={onCloseHandler}
								style={{ flex: '0 0 auto' }}
								cursor='pointer'
							/>
						</Card.Header>
						<Card.Body className="p-4">
							<Row className="mb-4">
								<Col>
									<div className="alert alert-info d-flex align-items-center">
										<FontAwesomeIcon icon={faInfoCircle} className="me-2" />
										<span>
											Please review the pricing details and any applied discounts before
											approving this order.
										</span>
									</div>
								</Col>
							</Row>
							<Row className="g-3">
								<Col md={4}>
									<Card className="border-success h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Our Cost</h6>
											<h4 className="text-success mb-0">
												<Money>{costSnapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>
									
								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Invoice Price</h6>
											<h4 className={`mb-0 ${successOrErrorCssClassInvoice}`}>
												{invoiceTotal !== null ? <Money>{invoiceTotal}</Money> : "Loading..."}</h4>
										</Card.Body>
									</Card>
								</Col>
								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Distributor Price</h6>
											<h4 className={`mb-0 ${successOrErrorCssClassDist}`}>
												<Money>{quote.dist_sale_price_snapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>
									
								<Col md={4}>
									<Card className="border-primary h-100">
										<Card.Body className="text-center">
											<h6 className="text-muted mb-2">Dealer Price</h6>
											<h4 className={`mb-0 ${successOrErrorCssClassDealer}`}>
												<Money>{quote.dealer_sale_price_snapshot}</Money>
											</h4>
										</Card.Body>
									</Card>
								</Col>
							</Row>

							{showCouponInfo
								&& (
									<Row className="mt-4">
										<Col>
											<Card className="bg-body-secondary">
												<Card.Body>
													<h6 className="mb-2">
														<FontAwesomeIcon
															icon={faInfoCircle}
															className="text-muted me-2"
														/>
														Discount Information
													</h6>
													{hasCouponDiscount && (
														<Row className="mb-2">
															<Col md={4}>
																<strong>Coupon Applied:</strong>
															</Col>
															<Col md={4}>
																{quote.coupon_name || 'Coupon Applied'}
															</Col>
															<Col md={4}>
																<span className="text-success">
																	-
																	<Money>
																		{quote.coupon_snapshot}
																	</Money>
																</span>
															</Col>
														</Row>
													)}
													{hasDiscountReason && (
														<Row>
															<Col md={4}>
																<strong>Discount Reason:</strong>
															</Col>
															<Col md={8}>
																<span className="text-muted">
																	{quote.discount_reason}
																</span>
															</Col>
														</Row>
													)}
												</Card.Body>
											</Card>
										</Col>
									</Row>
								)}

							<Row className="mt-4">
								<Col>
									<Button
										color="secondary"
										onClick={() => setQuoteCostBreakdownOpen(true)}
										className="me-2"
									>
										View Cost Breakdown
									</Button>
								</Col>
							</Row>

							<Row className="mt-4 pt-3 border-top">
								<Col>
									<div className="d-flex justify-content-between gap-3">
										
										
										<Button
											color="green"
											onClick={handleRejectOrder}
											disabled={approving}
											loading={rejecting}
										>
											{rejecting ? 'Rejecting...' : 'Reject Order'}
										</Button>
										<Button
											color="green"
											onClick={handleApproveOrder}
											disabled={rejecting}
											loading={approving}
										>
											{approving ? 'Approving...' : 'Approve Order'}
										</Button>
									</div>
								</Col>
							</Row>
						</Card.Body>
					</Card>
				</Container>
			)}
		</Popup>
	);
};

export default ApprovalModal;
